import { v4 as uuidv4 } from 'uuid'
import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { Replace, NominalUUID, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { OngoingReport } from '../../../shared/domain/reports/ongoingReport.no-deps'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import type { SyncProcedure } from '../procedure/Procedure'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import type { TBaseEvent } from '../../../shared/domain/calendarEvents/types.no-deps'
import type { CachedUnitData } from '../../../shared/domain/units/CachedUnitData.no-deps'

export type FlowReportStatus = 'open' | 'completed' | 'cancelled' | 'unassigned'

/**
 * This stores info about a report that being worked on locally in the app.
 */
export type FlowReport<Workflow extends WorkflowReference> = Replace<
    Omit<OngoingReport, 'clientId' | 'post'>,
    {
        id: ReportClientId
        serverId?: number
        status: FlowReportStatus
        procedure?: SyncProcedure
        workflow: Workflow
        postId?: UUID
        title: string
        completedDate?: Date
        cancelledDate?: Date
        unassignedDate?: Date
        previousReport?: FlowContinuationId
        uploadState:
            | 'notReadyToUpload'
            | 'pendingUpload'
            | 'uploaded'
            | 'pendingCancel'
            | 'pendingFinalisation'
            | 'pendingUploadAndFinalisation'
            | 'neverUpload'
        collaborators?: {
            invited: number[]
            accepted: number[]
        }
        lastSyncTime: Date
        location?: number
        calendarEvent?: TBaseEvent
        doesNotNeedUpdates?: boolean
    }
>

export type FlowContinuationId = NominalUUID<'FlowContinuationId'>
export const createContinuationId = () => uuidv4() as FlowContinuationId

/**
 * A continuation is enough information in order to open a report back where you left
 */
export interface FlowContinuation {
    continuationId: FlowContinuationId
    reportId: Flows.ReportId
    sectionId: Flows.ElementId
    scrollTop: number
}

//
// ReportUpdate

export type FlowReportUpdate = OpenStateChangeUpdate | NewAnswersUpdate | ReportSynced | UnitDataChange

export interface OpenStateChangeUpdate {
    type: 'OpenStateChangeUpdate'
    reportId: Flows.ReportId
    openState: FlowReportStatus
}

export interface NewAnswersUpdate {
    type: 'NewAnswersUpdate'
    reportId: Flows.ReportId
    newAnswers: Flows.Answer[]
}

export interface ReportSynced {
    type: 'ReportSynced'
    reportId: Flows.ReportId
    parentClientId?: Flows.ReportId
}

export interface UnitDataChange {
    type: 'UnitDataChange'
    reportId: Flows.ReportId
    cachedUnitData: CachedUnitData
}
